
import React, { useState, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { isValidEIN, isValidEmail, formatEIN } from '@/utils/validation';
import { validatePhoneNumber } from '@/utils/phoneValidation';
import { Button } from '@/components/ui/button';
import { CompanyData } from '@/types';
import OrganisationFormFields from './OrganisationFormFields';
import TermsOfServiceModal from '@/components/TermsOfServiceModal';

interface OrganisationDetailsFormProps {
  onSubmit: (data: CompanyData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialData?: CompanyData | null;
  organizationId?: string;
  isAdminEdit?: boolean; // Add this prop to indicate admin edit mode
}

const OrganisationDetailsForm: React.FC<OrganisationDetailsFormProps> = ({ 
  onSubmit, 
  onCancel,
  isSubmitting,
  initialData,
  organizationId,
  isAdminEdit = false // Default to false for backward compatibility
}) => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Initialize form data using the structure needed by OrganisationFormFields
  const [formData, setFormData] = useState<CompanyData>({
    pocName: '',
    pocEmail: '',
    pocPhone: '',
    pocPhoneVerified: false,
    name: '',
    name_dbas: '',
    address: '',
    stateOfIncorporation: '',
    ein: '',
    products: [{ id: nanoid(), name: '', description: '', category: '' }],
    terms: false
  });

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      // Ensure products have IDs
      const productsWithIds = initialData.products.map(product => ({
        ...product,
        id: product.id || nanoid()
      }));
      
      setFormData({ 
        ...initialData,
        products: productsWithIds,
        pocPhone: initialData.pocPhone || '',
        pocPhoneVerified: initialData.pocPhoneVerified || false
      });
    }
  }, [initialData]);

  const [formErrors, setFormErrors] = useState({
    pocName: false,
    pocEmail: false,
    pocPhone: false,
    name: false,
    address: false,
    stateOfIncorporation: false,
    ein: false,
    products: false,
    terms: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    const updatedFormData = {
      ...formData,
      [name]: name === 'ein' ? formatEIN(value) : value
    };
    
    setFormData(updatedFormData);
    
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const handlePhoneChange = (phone: string) => {
    setFormData({
      ...formData,
      pocPhone: phone,
      pocPhoneVerified: false // Reset verification when phone changes
    });
    
    if (formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }
  };

  const handlePhoneVerificationComplete = (verified: boolean) => {
    setFormData({
      ...formData,
      pocPhoneVerified: verified
    });
    
    if (verified && formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }
  };

  const handleProductChange = (id: string, field: keyof (typeof formData.products)[0], value: string) => {
    const updatedProducts = formData.products.map(product => 
      product.id === id ? { ...product, [field]: value } : product
    );
    
    setFormData({ 
      ...formData, 
      products: updatedProducts 
    });
    
    if (formErrors.products && updatedProducts.some(p => p.name && p.description && p.category)) {
      setFormErrors({ ...formErrors, products: false });
    }
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { id: nanoid(), name: '', description: '', category: '' }],
    });
  };

  const removeProduct = (id: string) => {
    if (formData.products.length > 1) {
      setFormData({
        ...formData,
        products: formData.products.filter(product => product.id !== id),
      });
    } else {
      toast('You must have at least one product', {
        description: 'At least one product is required for the organization.'
      });
    }
  };

  const handleTermsCheckedChange = (checked: boolean) => {
    setFormData({
      ...formData,
      terms: checked
    });
  };

  const validateForm = (): boolean => {
    const newErrors = {
      pocName: !formData.pocName.trim(),
      pocEmail: !isValidEmail(formData.pocEmail),
      pocPhone: false, // Skip phone verification for admin edits
      name: !formData.name.trim(),
      address: !formData.address?.trim(),
      stateOfIncorporation: !formData.stateOfIncorporation,
      ein: !isValidEIN(formData.ein),
      products: false, // Not validated in this simplified version
      terms: !formData.terms
    };

    setFormErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.terms) {
      toast('Terms of Service Required', {
        description: "You must accept the Terms of Service to continue"
      });
      return;
    }

    if (validateForm()) {
      // Include organization ID when submitting
      const submissionData = {
        ...formData,
        organization_id: organizationId || formData.organization_id
      };
      onSubmit(submissionData);
    } else {
      toast('Validation Error', {
        description: "Please fix the highlighted fields and try again."
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      <OrganisationFormFields
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleProductChange={handleProductChange}
        addProduct={addProduct}
        removeProduct={removeProduct}
        onTermsCheckedChange={handleTermsCheckedChange}
        onPhoneChange={handlePhoneChange}
        onPhoneVerificationComplete={handlePhoneVerificationComplete}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        disableEin={organizationId ? true : false}
        isAdminEdit={isAdminEdit} // Pass the admin edit flag to form fields
      />
      
      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Next Step'
          )}
        </Button>
      </div>
      
      <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </form>
  );
};

export default OrganisationDetailsForm;
